import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef }
	from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AbstractControl, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';

import { BlobService } from '../../shared/utils/blob.service';
import { pageTransitions } from '../../shared/animations/page-transitions';
import { UserService } from '../../shared/services/user.service';
import { User } from '../../shared/models/user';
import { HashService } from '../../shared/services/hash.service';
import { UNDERSCORE } from '../../shared/utils/constant';
import { SafeUrlPipe } from '../../shared/pipes/safeurl.pipe';
import { DragDropFilesDirective } from '../../shared/directives/drag-drop-files.directive';
import { ErrorService } from '../../shared/services/error.service';
import { SubmissionFeedbackComponent } from '../../shared/components/submission-feedback/submission-feedback.component';
import { TranslationService } from '../../shared/services/translation.service';
import {
	SnackbarSubmissionFeedbackComponent
} from '../../shared/components/snackbar-submission-feedback/snackbar-submission-feedback.component';
import { LoadingSpinnerComponent } from '../../shared/components/utils/loading-spinner.component';
import { Need4SteelService } from '../services/need4steel.api.service';

import { ContactMailData } from './contact-mail-content';

interface ContactMailFormModel {
	copyTo: string;
	messageSubject: string;
	textMessage: string;
}

function checkFileSize(file: File) {
	return file.size <= 1e+7;
}

@Component({
	selector: 'copo-elink',
	templateUrl: './contact-mail.component.html',
	styleUrl: './contact-mail.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		FormsModule,
		ReactiveFormsModule,

		MatFormFieldModule,

		MatInputModule,
		NgIf,

		MatButtonModule,
		MatIconModule,
		DragDropFilesDirective,
		SubmissionFeedbackComponent,
		NgFor,
		LoadingSpinnerComponent,
		SafeUrlPipe,
		MatDialogModule
	]
})
export class ContactMailComponent implements OnInit {
	contactMailForm: FormGroup;
	subject: string;
	isLoading = false;
	files: File[];
	fileUrls = new WeakMap<File, string>();
	readonly email: string;
	readonly firstname: string;
	readonly lastname: string;
	readonly gender?: string;
	readonly customer: string;
	readonly UNDERSCORE = UNDERSCORE;
	private readonly user?: User;

	constructor(

		private readonly dialogRef: MatDialogRef<ContactMailComponent>,

		@Inject(MAT_DIALOG_DATA) public data: ContactMailData,

		private readonly snackBar: MatSnackBar,
		private readonly userService: UserService,
		private readonly errorService: ErrorService,
		private readonly need4SteelService: Need4SteelService,
		private readonly translationService: TranslationService,
		private readonly blobService: BlobService,
		private readonly hashService: HashService,
	) {
		this.user = this.userService.user;
		this.email = this.user.email;
		this.firstname = this.user.firstname;
		this.lastname = this.user.lastname;
		this.gender = this.user.gender.charAt(0).toUpperCase() + this.user.gender.slice(1);
		this.customer = this.user.currentVisibility.organizationName;
		this.files = [];
		this.subject = this.constructSubject();
		this.contactMailForm = new FormGroup({
			copyTo: new FormControl(),
			messageSubject: new FormControl(),
			textMessage: new FormControl()
		});
	}

	onCancelClick() {
		this.errorService.resetErrorMsg();
		this.dialogRef.close();
	}

	ngOnInit() {
		this.contactMailForm = new FormGroup({
			copyTo: new FormControl('', [this.commaSepEmail]),
			messageSubject: new FormControl(),
			textMessage: new FormControl('', [Validators.required]),
		});
	}

	commaSepEmail = (control: AbstractControl) => {
		const formValue = <string>control.value;
		const emails = formValue.split(',').map(e => e.trim());
		const forbidden = emails.some(email => Boolean(Validators.email(new FormControl(email))));
		return forbidden ? { copyTo: { value: formValue } } : null;
	};

	async sendMessage(form: FormGroup) {
		try {
			this.isLoading = true;
			const attachmentMap = new Map<string, string>();
			for (const file of this.files) {
				const hash = await this.hashService.readSHA1(file);
				await firstValueFrom(this.need4SteelService.uploadFile(file, hash));
				attachmentMap.set(hash, file.name);
			}
			const mailContent = this.constructMailContent(form, attachmentMap);
			await firstValueFrom(this.need4SteelService.contactMill(mailContent));
			this.isLoading = false;
			this.onCancelClick();
			this.snackBar.openFromComponent(
				SnackbarSubmissionFeedbackComponent,
				{
					data: {
						hideResponseDetails: true,
						isThanksMessage: true,
						isRedirectLink: true,
						submitThanksMessage: this.translationService.getTR('contactmail.popup.success.msg-line1'),
						orderSavedText1: this.translationService.getTR('contactmail.popup.success.msg-line2'),
						followUpText: this.translationService.getTR('arcelormittal.need4steel.entry')
					},
					duration: 10_000,
					panelClass: ['copo-snackbar-submission-feedback-container']
				}
			);
			return true;
		} catch (err) {
			this.isLoading = false;
			if (!(err instanceof HttpErrorResponse)) {
				return false;
			}
			if (typeof err.error === 'object' && err.error !== null
				&& err.status === 400) {
				this.errorService.setErrorMsg(
					this.translationService.getTR('contactmail.submit.attachment.error.msg'),
					'quapp.error'
				);
			} else if (typeof err.error === 'object' && err.error !== null
				&& err.status === 403) {
				this.errorService.setErrorMsg(
					this.translationService.getTR('contactmail.submit.session.error.msg'),
					'quapp.error'
				);
			} else {
				this.errorService.setErrorMsg(
					this.translationService.getTR('contactmail.submit.error.msg'),
					'quapp.error'
				);
				console.error(err);
			}
			return false;
		}
	}

	uploadFile(events: FileList | null) {
		this.errorService.resetErrorMsg();
		if (!events) {
			return;
		}
		const files = Array.from(events);

		if ((this.files.length + events.length > 3)
			|| events.length > 3
			|| this.files.length >= 3) {
			this.errorService.setErrorMsg(
				this.translationService.getTR('n4steel.upload.file.size.error.message'),
				'quapp.error'
			);
			return;
		}
		for (const file of files) {
			if (checkFileSize(file)) {
				this.files.push(file);
			} else {
				this.errorService.setErrorMsg(
					this.translationService.getTR('n4steel.upload.file.size.error.message'),
					'quapp.error'
				);
			}
		}
	}

	deleteAttachment(index: number) {
		if (this.files.length === 1) {
			this.files = [];
			return;
		}
		this.files.splice(index, 1);
	}

	getFileUrl(file: File) {
		const url = this.fileUrls.get(file);
		if (url) {
			return url;
		}
		const newUrl = URL.createObjectURL(file);
		this.fileUrls.set(file, newUrl);
		return newUrl;
	}

	async showFile(event: MouseEvent, file: File) {
		const { href } = <HTMLAnchorElement>event.currentTarget;
		if (!href.startsWith('blob:') || !('msSaveOrOpenBlob' in navigator)) {
			return;
		}
		event.preventDefault();
		const blob = await this.blobService.getBlob(href);
		navigator.msSaveOrOpenBlob(blob, file.name);
	}

	private constructSubject() {
		return '[SteelUser]'.concat(' - ');
	}

	private constructMailContent(form: FormGroup, attachmentMap: Map<string, string>) {
		const formValue = <ContactMailFormModel>form.value;
		const copyToValue = formValue.copyTo;
		const subjectValue = formValue.messageSubject;
		const textMessageValue = formValue.textMessage;
		const subject = subjectValue ? this.subject.concat(' ').concat(subjectValue) : this.subject;

		const contacts = copyToValue ? copyToValue.split(',').map(e => e.trim()) : undefined;
		return {
			subject,
			messageContent: textMessageValue,
			mill: this.data.mill,
			offer: this.data.offer,
			customer: this.customer,
			contacts,
			attachments: Object.fromEntries(attachmentMap.entries())
		};
	}
}
